// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#egress-reachability
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { relativeTime } from '@/components/greenlight/relativeTime';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { HEALTH_POLL_MS } from '@/lib/healthPoll';
import { cn } from '@/lib/utils';
import type { EgressDestinationResult, EgressReachability } from '@greenlight/shared';

const QUERY_KEY = ['health-egress'];
const CHECKING_POLL_MS = 2_000;
/** The server's own floor: a check within the last minute is reused. */
const STALE_AFTER_MS = 60_000;

const STAGE_LABEL: Record<NonNullable<EgressDestinationResult['stage']>, string> = {
  blocked: 'refused by policy',
  dns: 'name resolution failed',
  tcp: 'no connection',
  tls: 'TLS failed',
  http: 'no answer',
};

type Applying = Extract<EgressReachability, { applies: true }>;

function summarize(state: Applying): string | null {
  if (state.checkedAt === null) return null;
  const blocked = state.destinations.filter((d) => d.status === 'blocked').length;
  return blocked === 0 ? 'All reachable' : `${blocked} blocked`;
}

function DestinationRow({
  destination,
}: {
  readonly destination: EgressDestinationResult;
}): React.JSX.Element {
  const blocked = destination.status === 'blocked';
  return (
    <li
      className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
      data-testid={`egress-row-${destination.group}-${destination.name}`}
    >
      <div className="min-w-0">
        <p className="truncate font-mono text-[13px] text-foreground">{destination.name}</p>
        <p className="text-[12px] text-muted-foreground">
          {destination.reason ?? destination.integrationName}
        </p>
        {blocked && destination.message !== null ? (
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {destination.address !== destination.name ? (
              <>
                <span className="break-all font-mono">{destination.address}</span> ·{' '}
              </>
            ) : null}
            {destination.message}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <span
          className={cn(
            'size-1.5 rounded-full',
            blocked ? 'bg-[hsl(var(--status-red))]' : 'bg-[hsl(var(--status-green))]',
          )}
          aria-hidden="true"
        />
        <span className="text-[13px] text-foreground">
          {blocked ? `Blocked · ${STAGE_LABEL[destination.stage ?? 'tcp']}` : 'Reachable'}
        </span>
      </div>
    </li>
  );
}

function Group({
  title,
  destinations,
  empty,
}: {
  readonly title: string;
  readonly destinations: readonly EgressDestinationResult[];
  readonly empty: string;
}): React.JSX.Element {
  return (
    <div>
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h3>
      {destinations.length === 0 ? (
        <p className="py-2.5 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y divide-border">
          {destinations.map((d) => (
            <DestinationRow key={`${d.address}-${d.integrationName ?? ''}`} destination={d} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Every destination the customer's firewall must allow, and whether the control
 * plane reached each at the last check. Renders nothing unless this install's
 * egress leaves through that firewall. Opening it starts a check when the last
 * is over a minute old; nothing else does.
 */
export function EgressSection(): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const { data, isError } = api.health.egressReachability.useQuery(QUERY_KEY, undefined, {
    queryKey: QUERY_KEY,
    refetchInterval: (query) => {
      const body = query.state.data?.status === 200 ? query.state.data.body : undefined;
      return body?.applies && body.checking ? CHECKING_POLL_MS : HEALTH_POLL_MS;
    },
  });
  const check = api.health.checkEgressReachability.useMutation();
  const [failed, setFailed] = React.useState(false);
  const autoStarted = React.useRef(false);

  const state = data?.status === 200 ? data.body : undefined;
  const readFailed = isError || (data !== undefined && data.status !== 200);

  const start = React.useCallback((): void => {
    setFailed(false);
    check.mutate(
      {},
      {
        onSuccess: (result) => {
          if (result.status !== 200) {
            setFailed(true);
            return;
          }
          queryClient.setQueryData(QUERY_KEY, result);
        },
        onError: () => setFailed(true),
      },
    );
  }, [check, queryClient]);

  const stale =
    state?.applies === true &&
    !state.checking &&
    (state.checkedAt === null || Date.now() - Date.parse(state.checkedAt) > STALE_AFTER_MS);

  React.useEffect(() => {
    if (stale && !autoStarted.current) {
      autoStarted.current = true;
      start();
    }
  }, [stale, start]);

  if (readFailed) {
    return (
      <SectionCard title="Egress" className="md:col-span-2" testId="egress-section">
        <Alert variant="destructive" data-testid="egress-error">
          <AlertDescription>
            Greenlight could not read the egress check. Reload the page to try again.
          </AlertDescription>
        </Alert>
      </SectionCard>
    );
  }
  if (state === undefined || !state.applies) return null;

  const checking = state.checking || check.isPending;
  const greenlight = state.destinations.filter((d) => d.group === 'greenlight');
  const integrations = state.destinations.filter((d) => d.group === 'integration');
  const summary = summarize(state);

  return (
    <SectionCard
      title="Egress"
      className="md:col-span-2"
      testId="egress-section"
      badge={
        summary !== null ? (
          <span className="text-xs text-muted-foreground">{summary}</span>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          This install&rsquo;s outbound traffic leaves through your firewall. Allow each destination
          below from the Greenlight network; a blocked row names the stage that failed.
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground" data-testid="egress-checked">
            {checking
              ? 'Checking each destination…'
              : state.checkedAt !== null
                ? `Checked ${relativeTime(state.checkedAt)}`
                : state.lastCheckFailed
                  ? 'No check has finished yet'
                  : 'Not checked yet'}
          </p>
          <Button variant="secondary" className="h-11 md:h-9" disabled={checking} onClick={start}>
            Check again
          </Button>
        </div>

        {failed ? (
          <Alert variant="destructive">
            <AlertDescription>The check could not be started.</AlertDescription>
          </Alert>
        ) : null}

        {state.lastCheckFailed && !checking ? (
          <Alert variant="destructive" data-testid="egress-check-failed">
            <AlertDescription>
              The last check failed before it finished.
              {state.checkedAt !== null ? ' The results below are from the check before it.' : ''}
            </AlertDescription>
          </Alert>
        ) : null}

        {state.checkedAt === null ? null : (
          <>
            <Group
              title="Greenlight"
              destinations={greenlight}
              empty="No Greenlight destinations were checked."
            />
            <Group
              title="Integrations"
              destinations={integrations}
              empty="No integrations are registered."
            />
          </>
        )}
      </div>
    </SectionCard>
  );
}
